import { createReducer } from "@reduxjs/toolkit";
import { isEqual } from "lodash";
import type {
  APIResponse,
  PimFields,
  PimFieldValue,
  Product,
  Variant,
} from "@thenounproject/lingo-core";
import { uploadFile } from "@actions/uploads/uploadFile";
import {
  buildVariantMatrix,
  extractOptionsFromVariants,
  extractMarketUuidsFromVariants,
  type ProductOptionData,
  type LocalVariant,
} from "@features/products/helpers/variantHelpers";
import { envelopesEqual, makeAssets, withSource } from "@features/products/productTypeFields";
import { initProduct } from "@actions/products/edit/useInitProduct";
import { setProductFieldValue } from "@actions/products/edit/useSetProductFieldValue";
import { setProductOptions } from "@actions/products/edit/useSetProductOptions";
import { setProductAssignedMarkets } from "@actions/products/edit/useSetProductAssignedMarkets";
import { setProductVariants } from "@actions/products/edit/useSetProductVariants";
import { setProductAssetUuids } from "@actions/products/edit/useSetProductAssetUuids";
import { revertProduct } from "@actions/products/edit/useRevertProduct";
import { clearProduct } from "@actions/products/edit/useClearProduct";
import { appendProductAsset } from "@actions/products/edit/useAppendProductAsset";
import { removeProductAsset } from "@actions/products/edit/useRemoveProductAsset";
import { reorderProductAsset } from "@actions/products/edit/useReorderProductAsset";
import { setVariantStatus } from "@actions/products/edit/useSetVariantStatus";
import { setVariantFieldValue } from "@actions/products/edit/useSetVariantFieldValue";
import { commitVariantFieldValue } from "@actions/products/edit/useCommitVariantFieldValue";
import { addProductOption } from "@actions/products/edit/useAddProductOption";
import { updateProductOption } from "@actions/products/edit/useUpdateProductOption";
import { deleteProductOption } from "@actions/products/edit/useDeleteProductOption";
import { fetchProduct } from "@actions/products/useProduct";
import { updateProduct } from "@actions/products/useUpdateProduct";
import { deleteAssets } from "@actions/assets/useDeleteAssets";
import { getEntitiesFromAction } from "./helpers/mergeIfChanged";

export type ProductEditSnapshot = {
  fields: PimFields;
  options: ProductOptionData[];
  assignedMarketUuids: string[];
  variants: LocalVariant[];
  serverVariants: Variant[];
};

export type ProductEditEntry = {
  productUuid: string;
  spaceId: number;
  productDateUpdated: string;
  isDraftRestored: boolean;
  serverUpdatedSince: boolean;
  fields: PimFields;
  options: ProductOptionData[];
  assignedMarketUuids: string[];
  variants: LocalVariant[];
  snapshot: ProductEditSnapshot;
  // requestId → { variantKey, identifier } for in-flight uploads
  pendingUploads: Record<string, { variantKey: string | null; identifier: string }>;
};

export type ProductEditState = Record<string, ProductEditEntry>;

function cloneFields(fields: PimFields): PimFields {
  // JSON clone rather than structuredClone: this runs inside immer's produce,
  // so `fields` is often a draft proxy that structuredClone refuses. Envelopes
  // are all plain data, so JSON round-trips are safe and cheap.
  return JSON.parse(JSON.stringify(fields)) as PimFields;
}

function cloneVariants(variants: LocalVariant[]): LocalVariant[] {
  return variants.map(v => ({ ...v, fields: cloneFields(v.fields) }));
}

function buildServerState(product: Product) {
  const serverVariants = product.variants ?? [],
    options = extractOptionsFromVariants(serverVariants),
    assignedMarketUuids = extractMarketUuidsFromVariants(serverVariants),
    fields = cloneFields(product.fields ?? {}),
    variants = buildVariantMatrix(options, assignedMarketUuids, [], serverVariants, fields);
  return { options, assignedMarketUuids, variants, fields, serverVariants };
}

/** Mutate `envelope.assets` via `fn`, creating an override envelope on a variant if needed. */
function mutateAssetEnvelope(
  target: { fields: PimFields },
  identifier: string,
  fn: (uuids: string[]) => string[]
) {
  const current = target.fields[identifier];
  const existing = current && "assets" in current ? current.assets.map(a => a.assetUuid) : [];
  const next = fn(existing);
  target.fields[identifier] = makeAssets(next, "override");
}

/** Mutate the product (variantKey=null) or variant's asset envelope. */
function mutateAssetEnvelopeFor(
  entry: ProductEditEntry,
  variantKey: string | null,
  identifier: string,
  fn: (uuids: string[]) => string[]
) {
  if (variantKey) {
    const variant = entry.variants.find(v => v.key === variantKey);
    if (variant) mutateAssetEnvelope(variant, identifier, fn);
  } else {
    // Product-level: source is always null
    const current = entry.fields[identifier];
    const existing = current && "assets" in current ? current.assets.map(a => a.assetUuid) : [];
    const next = fn(existing);
    entry.fields[identifier] = makeAssets(next, null);
  }
}

export default createReducer<ProductEditState>({}, builder => {
  builder
    .addCase(initProduct, (state, action) => {
      const { product, spaceId, draft } = action.payload;
      const existing = state[product.uuid];

      if (existing && existing.productDateUpdated === product.dateUpdated) return;

      const server = buildServerState(product);
      const snapshot: ProductEditSnapshot = {
        fields: cloneFields(server.fields),
        options: [...server.options],
        assignedMarketUuids: [...server.assignedMarketUuids],
        variants: cloneVariants(server.variants),
        serverVariants: server.serverVariants,
      };

      const validDraft =
        draft && new Date(product.dateUpdated).getTime() <= draft.savedAt ? draft : null;

      state[product.uuid] = {
        productUuid: product.uuid,
        spaceId,
        productDateUpdated: product.dateUpdated,
        isDraftRestored: validDraft !== null,
        serverUpdatedSince: false,
        fields: validDraft?.fields ? cloneFields(validDraft.fields) : server.fields,
        options: validDraft?.options ?? server.options,
        assignedMarketUuids: validDraft?.assignedMarketUuids ?? server.assignedMarketUuids,
        variants: validDraft?.variants ?? server.variants,
        snapshot,
        pendingUploads: {},
      };
    })

    .addCase(setProductFieldValue, (state, action) => {
      const { productUuid, identifier, value } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      entry.fields[identifier] = { ...value, source: null };
    })

    .addCase(setProductOptions, (state, action) => {
      const { productUuid, options } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      entry.options = options;
      entry.variants = buildVariantMatrix(
        options,
        entry.assignedMarketUuids,
        entry.variants,
        entry.snapshot.serverVariants,
        entry.fields
      );
    })

    .addCase(setProductAssignedMarkets, (state, action) => {
      const { productUuid, assignedMarketUuids } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      entry.assignedMarketUuids = assignedMarketUuids;
      entry.variants = buildVariantMatrix(
        entry.options,
        assignedMarketUuids,
        entry.variants,
        entry.snapshot.serverVariants,
        entry.fields
      );
    })

    .addCase(setProductVariants, (state, action) => {
      const { productUuid, variants } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      entry.variants = variants;
    })

    .addCase(setProductAssetUuids, (state, action) => {
      const { productUuid, variantKey, identifier, assetUuids } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      mutateAssetEnvelopeFor(entry, variantKey, identifier, () => assetUuids);
    })

    .addCase(revertProduct, (state, action) => {
      const { productUuid } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      const { snapshot } = entry;
      entry.fields = cloneFields(snapshot.fields);
      entry.options = [...snapshot.options];
      entry.assignedMarketUuids = [...snapshot.assignedMarketUuids];
      entry.variants = cloneVariants(snapshot.variants);
      entry.isDraftRestored = false;
    })

    .addCase(updateProduct.fulfilled, (state, action) => {
      const productUuid = action.meta.arg.id;
      const entry = state[productUuid];
      if (!entry) return;
      entry.snapshot = {
        ...entry.snapshot,
        fields: cloneFields(entry.fields),
        options: [...entry.options],
        assignedMarketUuids: [...entry.assignedMarketUuids],
        variants: cloneVariants(entry.variants),
      };
      entry.isDraftRestored = false;
      entry.serverUpdatedSince = false;
    })

    .addCase(fetchProduct.fulfilled, (state, action) => {
      const productUuid = action.payload.result as string;
      const product = getEntitiesFromAction<Product>(action, "products")?.[productUuid];
      if (!product) return;
      const entry = state[productUuid];
      if (!entry) return;
      if (entry.productDateUpdated === product.dateUpdated) return;

      const server = buildServerState(product);

      // Silently apply server values for any field the user hasn't touched.
      for (const [identifier, serverValue] of Object.entries(server.fields)) {
        const isClean = envelopesEqual(entry.fields[identifier], entry.snapshot.fields[identifier]);
        if (isClean) entry.fields[identifier] = serverValue as PimFieldValue;
      }

      const entryVariantsStr = JSON.stringify(entry.variants),
        variantsClean = entryVariantsStr === JSON.stringify(entry.snapshot.variants),
        serverVariantClones = cloneVariants(server.variants);
      if (isEqual(entry.options, entry.snapshot.options)) entry.options = [...server.options];
      if (isEqual(entry.assignedMarketUuids, entry.snapshot.assignedMarketUuids))
        entry.assignedMarketUuids = [...server.assignedMarketUuids];
      if (variantsClean) entry.variants = serverVariantClones;

      entry.snapshot = {
        ...entry.snapshot,
        fields: cloneFields(server.fields),
        options: [...server.options],
        assignedMarketUuids: [...server.assignedMarketUuids],
        variants: serverVariantClones,
        serverVariants: server.serverVariants,
      };
      entry.productDateUpdated = product.dateUpdated;

      // Flag if any dirty working field now diverges from the updated server field.
      entry.serverUpdatedSince =
        Object.keys(entry.fields).some(
          id => !envelopesEqual(entry.fields[id], server.fields[id])
        ) ||
        (!variantsClean && entryVariantsStr !== JSON.stringify(server.variants));
    })

    .addCase(appendProductAsset, (state, action) => {
      const { productUuid, variantKey, identifier, assetUuid } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      mutateAssetEnvelopeFor(entry, variantKey, identifier, uuids =>
        uuids.includes(assetUuid) ? uuids : [...uuids, assetUuid]
      );
    })

    .addCase(removeProductAsset, (state, action) => {
      const { productUuid, variantKey, identifier, assetUuid } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      mutateAssetEnvelopeFor(entry, variantKey, identifier, uuids =>
        uuids.filter(id => id !== assetUuid)
      );
    })

    .addCase(reorderProductAsset, (state, action) => {
      const { productUuid, variantKey, identifier, fromUuid, toUuid } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      mutateAssetEnvelopeFor(entry, variantKey, identifier, uuids => {
        const fromIndex = uuids.indexOf(fromUuid),
          toIndex = uuids.indexOf(toUuid);
        if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return uuids;
        const next = [...uuids];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return next;
      });
    })

    .addCase(setVariantStatus, (state, action) => {
      const { productUuid, variantKey, status } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      const variant = entry.variants.find(v => v.key === variantKey);
      if (variant) variant.status = status;
    })

    .addCase(setVariantFieldValue, (state, action) => {
      const { productUuid, variantKey, identifier, value } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      const variant = entry.variants.find(v => v.key === variantKey);
      if (!variant) return;
      variant.fields[identifier] = { ...value, source: "override" };
    })

    .addCase(commitVariantFieldValue, (state, action) => {
      const { productUuid, variantKey, identifier, value } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      const variant = entry.variants.find(v => v.key === variantKey);
      if (!variant) return;
      const productValue = entry.fields[identifier];
      // If the committed envelope matches the product's, collapse to inherited.
      if (
        productValue &&
        envelopesEqual({ ...value, source: null }, { ...productValue, source: null })
      ) {
        variant.fields[identifier] = withSource(productValue, "inherited");
      } else {
        variant.fields[identifier] = { ...value, source: "override" };
      }
    })

    .addCase(addProductOption, (state, action) => {
      const { productUuid, id } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      const newOptions = [...entry.options, { id, name: "", values: [] }];
      entry.options = newOptions;
      entry.variants = buildVariantMatrix(
        newOptions,
        entry.assignedMarketUuids,
        entry.variants,
        entry.snapshot.serverVariants,
        entry.fields
      );
    })

    .addCase(updateProductOption, (state, action) => {
      const { productUuid, index, option } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      const newOptions = entry.options.map((o, i) => (i === index ? option : o));
      entry.options = newOptions;
      entry.variants = buildVariantMatrix(
        newOptions,
        entry.assignedMarketUuids,
        entry.variants,
        entry.snapshot.serverVariants,
        entry.fields
      );
    })

    .addCase(deleteProductOption, (state, action) => {
      const { productUuid, index } = action.payload;
      const entry = state[productUuid];
      if (!entry) return;
      const newOptions = entry.options.filter((_, i) => i !== index);
      entry.options = newOptions;
      entry.variants = buildVariantMatrix(
        newOptions,
        entry.assignedMarketUuids,
        entry.variants,
        entry.snapshot.serverVariants,
        entry.fields
      );
    })

    .addCase(deleteAssets.fulfilled, (state, action) => {
      const { assetIds } = action.meta.arg;
      if (!assetIds.length) return;
      const deletedSet = new Set(assetIds);

      // Remove deleted asset UUIDs from every asset-typed envelope in every entry/variant/snapshot.
      const scrubFields = (fields: PimFields): PimFields => {
        let changed = false;
        const next: PimFields = {};
        for (const [id, v] of Object.entries(fields)) {
          if (v && "assets" in v) {
            const kept = v.assets.filter(a => !deletedSet.has(a.assetUuid));
            if (kept.length !== v.assets.length) {
              changed = true;
              next[id] = { ...v, assets: kept };
              continue;
            }
          }
          next[id] = v;
        }
        return changed ? next : fields;
      };

      Object.values(state).forEach(entry => {
        entry.fields = scrubFields(entry.fields);
        entry.variants = entry.variants.map(v => ({ ...v, fields: scrubFields(v.fields) }));
        entry.snapshot.fields = scrubFields(entry.snapshot.fields);
        entry.snapshot.variants = entry.snapshot.variants.map(v => ({
          ...v,
          fields: scrubFields(v.fields),
        }));
      });
    })

    .addCase(clearProduct, (state, action) => {
      delete state[action.payload.productUuid];
    })

    .addCase(uploadFile.pending, (state, action) => {
      const { insertPosition } = action.meta.arg.upload;
      if (!insertPosition?.productId || !insertPosition.identifier) return;
      const entry = state[insertPosition.productId];
      if (!entry) return;
      entry.pendingUploads[action.meta.requestId] = {
        variantKey: insertPosition.variantKey ?? null,
        identifier: insertPosition.identifier,
      };
    })

    .addCase(uploadFile.rejected, (state, action) => {
      const { insertPosition } = action.meta.arg.upload;
      if (!insertPosition?.productId) return;
      const entry = state[insertPosition.productId];
      if (!entry) return;
      delete entry.pendingUploads[action.meta.requestId];
    })

    .addCase(uploadFile.fulfilled, (state, action) => {
      const { insertPosition } = action.meta.arg.upload;
      if (!insertPosition?.productId || !insertPosition.identifier) return;
      const assetUuid = (action.payload as APIResponse<string> | undefined)?.result;
      if (!assetUuid) return;
      const { productId: productUuid, variantKey = null, identifier } = insertPosition;
      const entry = state[productUuid];
      if (!entry) return;
      delete entry.pendingUploads[action.meta.requestId];
      mutateAssetEnvelopeFor(entry, variantKey, identifier, uuids =>
        uuids.includes(assetUuid) ? uuids : [...uuids, assetUuid]
      );
    });
});
